<template>
  <div class="lg:grid lg:grid-cols-[15.625rem_1fr]">
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[110] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-neutral-700 focus:shadow-md"
    >
      {{ t('nav.skip_to_content') }}
    </a>

    <NavigationSideBar
      :menu-items="filteredMenuItems"
      :user="currentUser"
      :settings="filteredMenuItems.filter((e) => e.name === 'nav.settings').length > 0"
      :show-settings="showSettings"
      :show-logout="props.showLogout"
      @logout="logout()"
    >
      <template v-if="$slots['sidebar-footer']" #sidebar-footer>
        <slot name="sidebar-footer" />
      </template>
    </NavigationSideBar>

    <!-- tabindex="-1": skip-link target must be able to receive focus -->
    <div id="main-content" tabindex="-1" class="min-w-0 focus:outline-none">
      <!-- Offset must match the topbar height (h-12 = 3rem) rendered in the content-header slot,
           so InfusePageLayout's h-[calc(100vh-var(--infuse-content-offset))] subtracts exactly that. -->
      <div
        v-if="$slots['content-header']"
        class="flex min-w-0 flex-col"
        style="--infuse-content-offset: 3rem; --infuse-page-padding-top: 1rem"
      >
        <slot name="content-header" />
        <router-view />
      </div>
      <router-view v-else />
    </div>

    <NavigationBottomBar
      :menu-items="filteredMenuItems"
      :show-settings="showSettings"
      :show-logout="props.showLogout"
      @logout="logout()"
    />

    <InfuseNotificationGroup v-slot="{ notifications, close }" group="notify-top">
      <InfuseNotification
        v-for="(notification, i) in notifications"
        :key="`t-infuse-notification-${i}`"
        :notification="notification"
        @click="close(i)"
      />
    </InfuseNotificationGroup>

    <InfuseNotificationGroup v-slot="{ notifications, close }" group="notify-bottom" position="bottom">
      <InfuseNotification
        v-for="(notification, i) in notifications"
        :key="`b-infuse-notification-${i}`"
        :notification="notification"
        @click="close(i)"
      />
    </InfuseNotificationGroup>
  </div>
</template>

<script lang="ts" setup>
import { useAuthenticator, User, useUserService } from '@infuse/auth/services';

import { InfuseNotification, InfuseNotificationGroup } from '@infuse/ui/components';
import { MenuItem } from '@infuse/ui/domain';
import { UiCurrentMenuInjectionToken } from '@infuse/ui/services';
import { computed, onMounted, provide, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouteLocationMatched, useRoute, useRouter } from 'vue-router';

import NavigationSideBar from './NavigationSideBar.vue';
import NavigationBottomBar from './NavigationBottomBar.vue';
import { useCurrentUserStore } from '@infuse/iam/user/store';

interface Props {
  /** Set false when the host app renders logout itself (e.g. in a topbar). */
  showLogout?: boolean;
}

const props = withDefaults(defineProps<Props>(), { showLogout: true });

const routeToMenuItem = (route: RouteLocationMatched): MenuItem => ({
  name: route.meta.name as string,
  icon: route.meta.icon as string,
  group: route.meta.group as string,
  permissionIds: route.meta.permissionIds as string[],
  permissionCheck: route.meta.permissionCheck as 'any' | 'all',
  to: route.path
});

const { t } = useI18n();
const currentUserStore = useCurrentUserStore();
const authenticator = useAuthenticator();
const route = useRoute();
const router = useRouter();
const userService = useUserService();

const currentUser = ref<User>();

const menuItems = computed(() => {
  const routes = router.getRoutes();

  return routes.filter((r) => r.meta && r.meta.name).map<MenuItem>(routeToMenuItem);
});

const filteredMenuItems = computed(() => {
  const routes = router.getRoutes();

  return routes.filter((r) => r.meta && r.meta.name && !r.meta.hidden).map<MenuItem>(routeToMenuItem);
});

const currentItem = computed(() => {
  const currentRoute = route.matched[route.matched.length - 1];
  const items = unref(menuItems);

  return items.find((i) => currentRoute.path.includes(i.to));
});

provide(UiCurrentMenuInjectionToken, currentItem);

async function logout() {
  await authenticator.logout();
  currentUserStore.reset();
  await router.push({ name: 'auth-login' });
}

onMounted(async () => {
  currentUser.value = await userService.getUser();
});

const showSettings = computed(() => {
  return router.getRoutes().find((route) => route.name === 'main')?.meta?.hideSettings !== true;
});
</script>
